import { sub } from 'date-fns';
import { upperFirst } from 'lodash';
import pluralize from 'pluralize';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@dotfile/frontend/shared/design-system';
import { formatDate } from '@dotfile/shared/common';
import {
  AnalysisResultModel,
  CHECK_ANALYSIS_RESULT_DOCUMENT_AGE_VALIDATION_KEY,
} from '@dotfile/shared/domain';

import { AnalysisResultVariant } from './types';
import { formatDateValue } from './utils';

export const AnalysisResultReceivedExpected = ({
  variant,
  type,
  analysisResult,
  unit,
}: {
  variant?: AnalysisResultVariant;
  type: 'received' | 'expected';
  analysisResult: Partial<AnalysisResultModel>;
  unit?: string;
}) => {
  const { t, i18n } = useTranslation();
  const { value, absoluteDate, parsedValue, isValueANumber } = useMemo(() => {
    const rawValue = analysisResult[type];
    if (!rawValue) return { value: null };

    // Number
    const parsedValue = parseFloat(rawValue);
    const isValueANumber =
      !isNaN(parsedValue) && parsedValue.toString().length === rawValue.length;
    let absoluteDate = null;

    // Valid age
    if (
      analysisResult.key ===
        CHECK_ANALYSIS_RESULT_DOCUMENT_AGE_VALIDATION_KEY &&
      isValueANumber &&
      unit &&
      parsedValue
    ) {
      absoluteDate = formatDate(
        sub(new Date(), { [pluralize(unit)]: parsedValue }),
        { locale: i18n.language },
      );
    }

    // Date
    const value = formatDateValue(rawValue, i18n.language);

    return {
      value,
      absoluteDate,
      isValueANumber,
      parsedValue,
    };
  }, [analysisResult, type, unit, i18n.language]);

  if (!value) return null;

  const isClientPortalVariant = variant === 'client-portal';
  const textProps = {
    color: isClientPortalVariant ? 'black' : undefined,
    fontSize: isClientPortalVariant ? 'sm' : undefined,
  };

  const typeLabel = type === 'received' ? 'detected' : 'expected';

  const dateLabel = absoluteDate && unit && (
    <>
      {type === 'received' && absoluteDate}{' '}
      {isValueANumber
        ? t(`document_analysis.age_day_${type}`, {
            ns: 'components',
            count: parsedValue,
            defaultValue: `(${parsedValue} ${pluralize(unit, parsedValue)}old)`,
          })
        : value}
    </>
  );

  return (
    <Text as="span" {...textProps}>
      {t(`checks.document.${typeLabel}`, {
        defaultValue: upperFirst(typeLabel),
        ns: 'client-portal',
      })}
      :&nbsp;
      <Text {...textProps} as="span" color="blue.700">
        {absoluteDate ? (
          dateLabel
        ) : (
          <>
            {isValueANumber ? parsedValue : value}{' '}
            {isValueANumber && unit && pluralize(unit, parsedValue)}
          </>
        )}
      </Text>
    </Text>
  );
};
